/**
 * KI-Antwort Konfiguration — Admin-Seite (v2.9.8)
 * Schlanke Admin-UI fuer KI-Antwortvorschlag-Einstellungen.
 * Modell, Temperature, System-Prompt, Kontext-Laenge, Feature-Toggle.
 */
import { useState, useEffect, useCallback } from 'react';
import { Zap, Save, RefreshCw, AlertCircle, Check } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MODEL_OPTIONS = [
  { value: 'gpt-4o-mini', label: 'GPT-4o-mini (schnell, kosteneffizient)' },
  { value: 'gpt-4o', label: 'GPT-4o (leistungsstark)' },
];

export const AiReplyConfigPage = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [dirty, setDirty] = useState(false);

  const getToken = () => {
    const saved = localStorage.getItem('gb-sylt-user');
    if (saved) {
      try { return JSON.parse(saved).token; } catch { return null; }
    }
    return null;
  };

  const loadConfig = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = getToken();
      const res = await axios.get(`${API}/mail/ai-reply/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConfig(res.data.config);
      setDirty(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Konfiguration konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  const handleSave = async () => {
    if (!config) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const token = getToken();
      await axios.put(`${API}/mail/ai-reply/config`, config, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      setSuccess('Konfiguration gespeichert');
      setDirty(false);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
    setDirty(true);
    setSuccess(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!config) {
    return (
      <div className="p-6 text-center text-red-500">
        <AlertCircle className="h-8 w-8 mx-auto mb-2" />
        <p>{error || 'Konfiguration nicht verfuegbar'}</p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-6 space-y-6" data-testid="ai-reply-config-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-violet-100 dark:bg-violet-900/30 rounded-lg">
            <Zap className="h-5 w-5 text-violet-600 dark:text-violet-400" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">
              KI-Antwort Konfiguration
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Modell, Tonalitaet und Verhalten der KI-Antwortvorschlaege
            </p>
          </div>
        </div>
        <button
          onClick={handleSave}
          disabled={saving || !dirty}
          className="flex items-center gap-2 px-4 py-2 bg-violet-600 text-white text-sm font-medium rounded-lg hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          data-testid="save-ai-config-btn"
        >
          {saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Speichern
        </button>
      </div>

      {/* Messages */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-300">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          {error}
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-sm text-green-700 dark:text-green-300" data-testid="save-success-msg">
          <Check className="h-4 w-4 flex-shrink-0" />
          {success}
        </div>
      )}

      {/* Feature Toggle */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Feature aktiv</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              KI-Antwortvorschlaege fuer alle Nutzer ein-/ausschalten
            </p>
          </div>
          <button
            onClick={() => updateField('enabled', !config.enabled)}
            className={`relative w-12 h-6 rounded-full transition-colors ${
              config.enabled ? 'bg-violet-600' : 'bg-gray-300 dark:bg-gray-600'
            }`}
            data-testid="ai-enabled-toggle"
          >
            <span
              className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                config.enabled ? 'translate-x-6' : 'translate-x-0'
              }`}
            />
          </button>
        </div>
      </div>

      {/* Modell */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 space-y-3">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Modell</h2>
        <div className="space-y-2">
          {MODEL_OPTIONS.map(opt => (
            <label
              key={opt.value}
              className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                config.model === opt.value
                  ? 'border-violet-400 bg-violet-50 dark:bg-violet-900/20 dark:border-violet-600'
                  : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
              data-testid={`model-option-${opt.value}`}
            >
              <input
                type="radio"
                name="model"
                value={opt.value}
                checked={config.model === opt.value}
                onChange={() => updateField('model', opt.value)}
                className="h-4 w-4 text-violet-600"
              />
              <span className="text-sm text-gray-900 dark:text-white">{opt.label}</span>
            </label>
          ))}
        </div>
        <p className="text-xs text-gray-400 dark:text-gray-500">
          Aktuell: <span className="font-medium">{config.provider}/{config.model}</span>
        </p>
      </div>

      {/* Temperature */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Temperature</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Niedrig = praeziser, Hoch = kreativer
            </p>
          </div>
          <span className="text-sm font-mono font-bold text-violet-600 dark:text-violet-400 bg-violet-50 dark:bg-violet-900/30 px-2.5 py-1 rounded-lg" data-testid="temperature-value">
            {config.temperature?.toFixed(1)}
          </span>
        </div>
        <input
          type="range"
          min="0"
          max="1"
          step="0.1"
          value={config.temperature || 0.7}
          onChange={(e) => updateField('temperature', parseFloat(e.target.value))}
          className="w-full accent-violet-600"
          data-testid="temperature-slider"
        />
        <div className="flex justify-between text-xs text-gray-400">
          <span>0.0 Praezise</span>
          <span>0.5</span>
          <span>1.0 Kreativ</span>
        </div>
      </div>

      {/* Max Kontext */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 space-y-3">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Max. Kontextlaenge</h2>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Maximale Zeichenanzahl aus dem Mail-Inhalt, die an die KI gesendet wird
        </p>
        <div className="flex items-center gap-3">
          <input
            type="number"
            min={500}
            max={8000}
            step={500}
            value={config.max_mail_context_chars || 3000}
            onChange={(e) => updateField('max_mail_context_chars', parseInt(e.target.value) || 3000)}
            className="w-32 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
            data-testid="max-context-input"
          />
          <span className="text-xs text-gray-400">Zeichen (500 - 8000)</span>
        </div>
      </div>

      {/* System-Prompt */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 space-y-3">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-white">System-Prompt (Rolle & Stil)</h2>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Definiert die Identitaet und den Ton der KI-Antworten. Wird als Rollenbeschreibung an das Modell gesendet.
        </p>
        <textarea
          value={config.system_role_hint || ''}
          onChange={(e) => updateField('system_role_hint', e.target.value)}
          rows={4}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-white resize-y focus:ring-2 focus:ring-violet-500 focus:border-transparent"
          placeholder="z.B. Du bist ein professioneller Assistent..."
          data-testid="system-prompt-textarea"
        />
        <p className="text-xs text-gray-400">
          {(config.system_role_hint || '').length} Zeichen
        </p>
      </div>

      {/* Info */}
      <div className="bg-gray-50 dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 p-4 text-xs text-gray-500 dark:text-gray-400 space-y-1">
        <p>Aenderungen werden sofort wirksam. Kein Neustart erforderlich.</p>
        <p>Die KI erstellt nur Vorschlaege — kein automatisches Senden.</p>
      </div>
    </div>
  );
};
